import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RiArrowLeftLine,
    RiPlayLine,
    RiArrowDownSLine,
    RiCheckLine,
    RiBriefcaseLine,
} from 'react-icons/ri';
import GlassCard from '../../components/ui/v2/GlassCard';
import GlassButton from '../../components/ui/v2/GlassButton';
import CompanySearchInput from '../../components/interview/CompanySearchInput';
import { simulationService } from '../../services/simulationService';
import type { Persona } from '../../services/simulationService';
import type { Company } from '../../models/interview';
import { cn } from '../../utils/cn';

type ExperienceLevel = 'ENTRY' | 'MID' | 'SENIOR';

interface FormData {
    jobTitle: string;
    jobDescription: string;
    experienceLevel: ExperienceLevel;
    company: string | Company;
    companyWebsite: string;
    companyCountry: string;
    jobPostingUrl: string;
    interviewStage: string;
    language: string;
    persona: string;
    difficulty: 'normal' | 'challenging' | 'intense';
    timeLimitSeconds: number;
}

const EXPERIENCE_OPTIONS: { id: ExperienceLevel; label: string; sublabel: string }[] = [
    { id: 'ENTRY', label: 'Entry Level', sublabel: '0–2 years' },
    { id: 'MID', label: 'Mid Level', sublabel: '3–6 years' },
    { id: 'SENIOR', label: 'Senior', sublabel: '7+ years' },
];



const DIFFICULTY_OPTIONS = [
    { id: 'normal', label: 'Normal', desc: 'Balanced pace' },
    { id: 'challenging', label: 'Challenging', desc: 'Tougher follow-ups' },
    { id: 'intense', label: 'Intense', desc: 'No fluff, high pressure' },
] as const;

const TIME_OPTIONS = [
    { seconds: 300, label: '5 min' },
    { seconds: 600, label: '10 min' },
    { seconds: 900, label: '15 min' },
    { seconds: 1800, label: '30 min' },
    { seconds: 2700, label: '45 min' },
    { seconds: 3600, label: '60 min' },
];

const LANGUAGES = [
    { code: 'en-US', label: 'English (US)' },
    { code: 'en-GB', label: 'English (UK)' },
    { code: 'es-ES', label: 'Spanish' },
    { code: 'fr-FR', label: 'French' },
    { code: 'de-DE', label: 'German' },
];

export default function SimulationSetupScreen() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const totalSteps = 3;
    const [personas, setPersonas] = useState<Persona[]>([]);
    const [stages, setStages] = useState<{ id: string; label: string }[]>([]);
    const [isStarting, setIsStarting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [useJobDescription, setUseJobDescription] = useState(true);

    const [formData, setFormData] = useState<FormData>({
        jobTitle: '',
        jobDescription: '',
        experienceLevel: 'SENIOR',
        company: '',
        companyWebsite: '',
        companyCountry: '',
        jobPostingUrl: '',
        interviewStage: '',
        language: 'en-US',
        persona: '',
        difficulty: 'normal',
        timeLimitSeconds: 1800,
    });

    useEffect(() => {
        // Fetch Personas
        simulationService.getPersonas()
            .then(res => {
                // Deduplicate by ID
                const uniquePersonas = Array.from(new Map(res.personas.map(p => [p.id, p])).values());
                setPersonas(uniquePersonas);
                if (uniquePersonas.length > 0 && !formData.persona) {
                    setFormData(p => ({ ...p, persona: uniquePersonas[0].id }));
                }
            })
            .catch(err => console.error('Failed to fetch personas:', err));

        // Fetch Stages
        simulationService.getStages()
            .then(res => {
                // Deduplicate by ID
                const uniqueStages = Array.from(new Map(res.stages.map(s => [s.id, s])).values());
                setStages(uniqueStages);
                if (uniqueStages.length > 0 && !formData.interviewStage) {
                    setFormData(p => ({ ...p, interviewStage: uniqueStages[0].id }));
                }
            })
            .catch(err => console.error('Failed to fetch stages:', err));
    }, []);

    const set = (field: keyof FormData, value: any) =>
        setFormData(prev => ({ ...prev, [field]: value }));

    const companyName = (company: string | Company) =>
        (typeof company === 'string' ? company : company?.name || '').trim();

    const isStepValid = () => {
        // Backend requires setup_context.company.name, so it's required here too
        // despite historically being labeled optional in the UI.
        if (step === 1) return Boolean(formData.jobTitle) && Boolean(companyName(formData.company));
        if (step === 2) return Boolean(formData.interviewStage);
        return Boolean(formData.persona);
    };

    const nextStep = () => { if (step < totalSteps) setStep(s => s + 1); };
    const prevStep = () => { if (step > 1) setStep(s => s - 1); else navigate('/interview-mode'); };

    const handleStart = async () => {
        // Defense in depth: step 1's isStepValid() already blocks reaching this point
        // without a company name, but never send the request without one regardless.
        if (!formData.jobTitle || !companyName(formData.company)) {
            setStep(1);
            return;
        }

        try {
            setIsStarting(true);

            const companyPayload = typeof formData.company === 'string'
                ? { name: formData.company, website: formData.companyWebsite || undefined, country: formData.companyCountry || undefined, job_posting_url: formData.jobPostingUrl || undefined }
                : { name: (formData.company as Company).name, website: formData.companyWebsite || (formData.company as any).opencorporates_url || undefined, country: formData.companyCountry || (formData.company as any).jurisdiction_code || undefined, job_posting_url: formData.jobPostingUrl || undefined };

            const payload = {
                setup_context: {
                    job_title: formData.jobTitle,
                    company: companyPayload,
                    job_description: useJobDescription ? (formData.jobDescription || undefined) : undefined,
                    experience_level: useJobDescription ? undefined : formData.experienceLevel,
                    interview_stage: formData.interviewStage,
                },
                simulation_settings: {
                    persona: formData.persona || undefined,
                    difficulty: formData.difficulty,
                    time_limit_seconds: formData.timeLimitSeconds,
                    language: formData.language,
                },
            };

            const data = await simulationService.createSimulation(payload);
            // Store the session data for the room page to pick up
            sessionStorage.setItem(`sim_session_${data.simulation_id}`, JSON.stringify(data));
            navigate(`/simulation/${data.simulation_id}`);
        } catch (err) {
            console.error('Simulation setup failed:', err);
            alert('Could not start simulation. Please try again.');
        } finally {
            setIsStarting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0B0B0F] text-white font-sans overflow-hidden relative flex flex-col">
            {/* Subtle ambient gradient */}
            <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-indigo-900/8 to-transparent pointer-events-none" />

            {/* Header */}
            <header className="relative z-10 p-6 flex items-center justify-between max-w-7xl mx-auto w-full">
                <button onClick={prevStep} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                        <RiArrowLeftLine />
                    </div>
                    <span className="text-sm font-medium">Back</span>
                </button>

                {/* Step dots */}
                <div className="flex gap-2 items-center">
                    {[1, 2, 3].map(s => (
                        <div key={s} className={cn('h-1 rounded-full transition-all duration-300',
                            s === step ? 'w-8 bg-white' : s < step ? 'w-2 bg-white/40' : 'w-2 bg-white/10'
                        )} />
                    ))}
                </div>

                <div className="w-20" /> {/* Spacer */}
            </header>

            {/* Main Content */}
            <main className="relative z-10 w-full max-w-2xl mx-auto px-4 flex-1 flex flex-col justify-center pb-24">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.25 }}
                    >
                        <GlassCard className="p-8 space-y-7">

                            {/* ── Step 1: Role Details ── */}
                            {step === 1 && (
                                <div className="space-y-6">
                                    <div>
                                        <h2 className="text-2xl font-bold mb-1">Role Details</h2>
                                        <p className="text-gray-400 text-sm">Define the position you're practising for.</p>
                                    </div>

                                    {/* Job Title */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Job Title *</label>
                                        <input
                                            type="text"
                                            value={formData.jobTitle}
                                            onChange={e => set('jobTitle', e.target.value)}
                                            placeholder="e.g., Senior Product Manager"
                                            autoFocus
                                            className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-white/30 outline-none transition-colors"
                                        />
                                    </div>

                                    {/* Company */}
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Company *</label>
                                        <CompanySearchInput
                                            value={formData.company}
                                            onChange={val => {
                                                if (typeof val !== 'string' && val && (val as any).jurisdiction_code) {
                                                    setFormData(prev => ({ ...prev, company: val, companyCountry: (val as any).jurisdiction_code || '', companyWebsite: (val as any).opencorporates_url || '' }));
                                                } else {
                                                    set('company', val);
                                                }
                                            }}
                                            placeholder="e.g., Stripe"
                                        />
                                        <button
                                            onClick={() => setShowAdvanced(!showAdvanced)}
                                            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors mt-1"
                                        >
                                            {showAdvanced ? 'Hide' : 'Add'} URL, website, country
                                            <RiArrowDownSLine className={cn('transition-transform', showAdvanced && 'rotate-180')} />
                                        </button>
                                        <AnimatePresence>
                                            {showAdvanced && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden space-y-2 pt-1"
                                                >
                                                    <input type="text" value={formData.companyWebsite} onChange={e => set('companyWebsite', e.target.value)} placeholder="Company website" className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:border-white/30 outline-none" />
                                                    <input type="text" value={formData.jobPostingUrl} onChange={e => set('jobPostingUrl', e.target.value)} placeholder="Job posting URL" className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:border-white/30 outline-none" />
                                                    <input type="text" value={formData.companyCountry} onChange={e => set('companyCountry', e.target.value)} placeholder="Country (e.g. US, UK)" className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:border-white/30 outline-none" />
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    {/* JD or Experience toggle */}
                                    <div className="space-y-3">
                                        <div className="flex bg-black/20 p-1 rounded-xl border border-white/5">
                                            <button onClick={() => setUseJobDescription(true)} className={cn('flex-1 py-2 text-sm font-medium rounded-lg transition-all', useJobDescription ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300')}>Job Description</button>
                                            <button onClick={() => setUseJobDescription(false)} className={cn('flex-1 py-2 text-sm font-medium rounded-lg transition-all', !useJobDescription ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300')}>Experience Level</button>
                                        </div>
                                        {useJobDescription ? (
                                            <textarea
                                                value={formData.jobDescription}
                                                onChange={e => set('jobDescription', e.target.value)}
                                                placeholder="Paste job description..."
                                                rows={5}
                                                className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:border-white/30 outline-none resize-none"
                                            />
                                        ) : (
                                            <div className="grid grid-cols-3 gap-2">
                                                {EXPERIENCE_OPTIONS.map(opt => (
                                                    <button
                                                        key={opt.id}
                                                        onClick={() => set('experienceLevel', opt.id)}
                                                        className={cn('p-3 rounded-xl border text-left transition-all', formData.experienceLevel === opt.id ? 'border-white/40 bg-white/10' : 'border-white/5 bg-black/20 hover:border-white/15')}
                                                    >
                                                        <div className="text-sm font-semibold text-white">{opt.label}</div>
                                                        <div className="text-xs text-gray-500">{opt.sublabel}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Step 2: Interview Stage ── */}
                            {step === 2 && (
                                <div className="space-y-6">
                                    <div>
                                        <h2 className="text-2xl font-bold mb-1">Interview Type</h2>
                                        <p className="text-gray-400 text-sm">What kind of interview are you preparing for?</p>
                                    </div>

                                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                        {stages.length === 0 ? (
                                            <div className="text-center py-12 text-gray-500">Loading stages...</div>
                                        ) : (
                                            stages.map(stage => {
                                                const isActive = formData.interviewStage === stage.id;
                                                return (
                                                    <button
                                                        key={stage.id}
                                                        onClick={() => set('interviewStage', stage.id)}
                                                        className={cn(
                                                            'w-full flex items-center p-4 border rounded-xl text-left transition-all group',
                                                            isActive ? 'border-brand-500/50 bg-brand-500/10' : 'border-white/5 bg-black/20 hover:border-white/15 hover:bg-white/5'
                                                        )}
                                                    >
                                                        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center mr-4 shrink-0 transition-all shadow-lg', isActive ? 'bg-white text-black scale-105' : 'bg-white/5 text-gray-500 group-hover:text-gray-300')}>
                                                            <RiBriefcaseLine size={18} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className={cn('font-semibold transition-colors', isActive ? 'text-white' : 'text-gray-300 group-hover:text-white')}>{stage.label}</div>
                                                        </div>
                                                        {isActive && (
                                                            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="bg-brand-500 text-white rounded-full p-1 ml-2">
                                                                <RiCheckLine size={16} />
                                                            </motion.div>
                                                        )}
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Step 3: Simulation Settings ── */}
                            {step === 3 && (
                                <div className="space-y-6">
                                    <div>
                                        <h2 className="text-2xl font-bold mb-1">Interviewer Style</h2>
                                        <p className="text-gray-400 text-sm">Pick how you want to be challenged.</p>
                                    </div>

                                    {/* Persona Cards */}
                                    <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                                        {personas.length === 0 ? (
                                            <div className="text-gray-500 text-sm text-center py-6">Loading interviewers...</div>
                                        ) : (
                                            personas.map(p => {
                                                const isSelected = formData.persona === p.id;
                                                return (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => set('persona', p.id)}
                                                        className={cn(
                                                            'w-full flex items-start gap-4 p-4 border rounded-xl text-left transition-all',
                                                            isSelected ? 'border-white/40 bg-white/10' : 'border-white/5 bg-black/20 hover:border-white/15 hover:bg-white/5'
                                                        )}
                                                    >
                                                        {/* Avatar initial */}
                                                        <div className={cn('w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 mt-0.5', isSelected ? 'bg-white text-black' : 'bg-white/10 text-white')}>
                                                            {p.name[0]}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                <span className="font-semibold text-white">{p.name}</span>
                                                                <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full border', isSelected ? 'bg-white/20 border-white/30 text-white' : 'bg-white/5 border-white/10 text-gray-400')}>
                                                                    {p.tone_label}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-400 leading-relaxed">{p.tone}</p>
                                                        </div>
                                                        {isSelected && <RiCheckLine className="text-white shrink-0 mt-1" />}
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>

                                    {/* Difficulty + Duration + Language in a compact grid */}
                                    <div className="space-y-4 pt-2 border-t border-white/5">
                                        {/* Difficulty */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Difficulty</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                {DIFFICULTY_OPTIONS.map(d => (
                                                    <button
                                                        key={d.id}
                                                        onClick={() => set('difficulty', d.id)}
                                                        className={cn('py-2.5 px-3 rounded-xl border text-sm font-medium transition-all flex flex-col items-center gap-0.5', formData.difficulty === d.id ? 'bg-white text-black border-white' : 'bg-black/20 border-white/10 text-gray-400 hover:bg-white/5')}
                                                    >
                                                        <span>{d.label}</span>
                                                        <span className={cn('text-[10px] font-normal', formData.difficulty === d.id ? 'text-black/60' : 'text-gray-600')}>{d.desc}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Duration */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                {TIME_OPTIONS.map(t => (
                                                    <button
                                                        key={t.seconds}
                                                        onClick={() => set('timeLimitSeconds', t.seconds)}
                                                        className={cn('py-2.5 rounded-xl border text-sm font-medium transition-all', formData.timeLimitSeconds === t.seconds ? 'bg-white text-black border-white' : 'bg-black/20 border-white/10 text-gray-400 hover:bg-white/5')}
                                                    >
                                                        {t.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Language */}
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Language</label>
                                            <div className="relative">
                                                <select
                                                    value={formData.language}
                                                    onChange={e => set('language', e.target.value)}
                                                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white outline-none focus:border-white/30 appearance-none"
                                                >
                                                    {LANGUAGES.map(l => (
                                                        <option key={l.code} value={l.code} className="text-black">{l.label}</option>
                                                    ))}
                                                </select>
                                                <RiArrowDownSLine className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Footer CTA */}
                            <div className="pt-4 border-t border-white/5">
                                {step === totalSteps ? (
                                    <GlassButton
                                        onClick={handleStart}
                                        loading={isStarting}
                                        disabled={!isStepValid()}
                                        className="w-full py-4 text-base font-semibold !bg-white !text-black hover:!bg-gray-200 !border-none shadow-[0_0_20px_rgba(255,255,255,0.12)]"
                                    >
                                        Start Simulation <RiPlayLine className="ml-2" />
                                    </GlassButton>
                                ) : (
                                    <GlassButton
                                        onClick={nextStep}
                                        disabled={!isStepValid()}
                                        className="w-full py-4 text-base font-semibold"
                                    >
                                        Next <RiArrowLeftLine className="ml-2 rotate-180" />
                                    </GlassButton>
                                )}
                            </div>

                        </GlassCard>
                    </motion.div>
                </AnimatePresence>
            </main>
        </div>
    );
}
